import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography, Chip, Divider, Alert } from '@mui/material';
import { LockClock, ReceiptLong, Refresh, Logout } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import { useCountyBillingStatus } from '../../hooks';

export function BillingRequiredPage() {
  const navigate = useNavigate();
  const { status, config, daysRemaining, isTrial, isLoading, refetch } = useCountyBillingStatus();

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  // If billing came back active (e.g. provider renewed in another tab), get the user back to the dashboard.
  const goToDashboard = () => navigate('/dashboard', { replace: true });

  const isResolved = status === 'active' || status === 'expiring_soon' || status === 'due_today';

  const headline =
    status === 'no_config'
      ? 'Billing plan not configured'
      : status === 'suspended'
        ? 'Billing suspended'
        : status === 'expired'
          ? (isTrial ? 'Free trial ended' : 'Subscription expired')
          : 'Billing status';

  const description =
    status === 'no_config'
      ? "Your county doesn't have a billing plan set up yet. The system provider needs to configure one before you can use the platform."
      : status === 'suspended'
        ? 'Access to the platform has been suspended by the system provider. Citizens cannot make payments and admin functions are unavailable.'
        : isTrial
          ? 'Your county’s free trial period has ended. Citizens cannot make payments and admin functions are unavailable until the system provider activates a paid plan or extends the trial.'
          : 'Your county’s billing period has ended. Citizens cannot make payments and admin functions are unavailable until the service provider renews your subscription.';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 620, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : isResolved ? (
            <Stack spacing={2} alignItems="center" textAlign="center">
              <ReceiptLong sx={{ fontSize: 56, color: 'success.main' }} />
              <Typography variant="h5" fontWeight={700}>Billing is active</Typography>
              <Typography variant="body2" color="text.secondary">
                Your county{daysRemaining ? ` has ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} until next renewal` : ''}.
              </Typography>
              <Button variant="contained" onClick={goToDashboard}>Go to Dashboard</Button>
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <LockClock sx={{ fontSize: 56, color: 'error.main' }} />
                <Typography variant="h5" fontWeight={700}>{headline}</Typography>
                <Typography variant="body1" color="text.secondary">{description}</Typography>
              </Stack>

              {config && (
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Plan</Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={(config.type || '').replace('_', ' ')}
                        size="small"
                        sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                      />
                      {isTrial && <Chip label="Free Trial" size="small" color="success" sx={{ fontWeight: 700 }} />}
                    </Stack>
                  </Stack>
                  {config.ends_at && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">Ended</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {new Date(config.ends_at).toLocaleDateString()}
                      </Typography>
                    </Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">Current status</Typography>
                    <Chip
                      label={(status || '').replace('_', ' ')}
                      size="small"
                      color="error"
                      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                    />
                  </Stack>
                </Box>
              )}

              <Alert severity="info" variant="standard" sx={{ bgcolor: 'info.bg', color: 'info.dark' }}>
                Please contact your service provider to resolve this. Once it&rsquo;s fixed, click <strong>Re-check</strong> and you&rsquo;ll be sent back to the dashboard.
              </Alert>

              <Divider />

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="contained" startIcon={<Refresh />} onClick={() => refetch()}>
                  Re-check
                </Button>
                <Button variant="outlined" startIcon={<Logout />} onClick={logout}>
                  Log out
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
